import React, { useState, useEffect } from "react";
import { ChefHat } from "lucide-react";

// Импортируем клиент Supabase
import { supabase } from "./supabaseClient"; 

import { TRANSLATIONS } from "./data/constants";
import { useIsMobile } from "./utils/helpers";
import { createStyles } from "./styles";
import PrintStyles from "./styles/PrintStyles";

import TopBar from "./components/common/TopBar";
import AdminPanel from "./components/AdminPanel";
import BanquetEditor from "./components/BanquetEditor";
import BanquetList from "./components/BanquetList";

export default function App() {
  const isMobile = useIsMobile();
  
  // 1. Инициализируем язык из памяти
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("app_lang") || "ro";
  });

  // 2. Сохраняем язык при каждом изменении
  useEffect(() => {
    localStorage.setItem("app_lang", lang);
  }, [lang]);

  const [isDark, setIsDark] = useState(false);
  
  const styles = createStyles(isMobile, isDark); 
  const t = TRANSLATIONS[lang];

  // --- Сессия Supabase ---
  const [session, setSession] = useState(null);
  const [view, setView] = useState("list");

  const [banquets, setBanquets] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [halls, setHalls] = useState([]);
  
  const [isLoading, setIsLoading] = useState(false);

  const [companyLogo, setCompanyLogo] = useState(() => {
    return localStorage.getItem("company_logo") || null;
  });

  const [currentBanquet, setCurrentBanquet] = useState(null);

  // --- ПРОВЕРКА АВТОРИЗАЦИИ ПРИ ЗАПУСКЕ ---
  useEffect(() => {
    // Проверяем сессию
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchData();
    });

    // Слушаем изменения
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchData();
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchData() {
    setIsLoading(true);
    try {
      const [
        { data: dishesData },
        { data: categoriesData },
        { data: hallsData },
        { data: banquetsData },
        { data: settingsData } // Загружаем настройки
      ] = await Promise.all([
        supabase.from('dishes').select('*'),
        supabase.from('categories').select('*').order('id'),
        supabase.from('halls').select('*').order('id'),
        supabase.from('banquets').select(`*, banquet_items (*)`).order('created_at', { ascending: false }),
        supabase.from('settings').select('*').eq('id', 1).maybeSingle() // Используем maybeSingle
      ]);

      if (dishesData) setDishes(dishesData);
      if (categoriesData) setCategories(categoriesData);
      if (hallsData) setHalls(hallsData);

      // УСТАНАВЛИВАЕМ ЛОГОТИП ИЗ БАЗЫ
      if (settingsData && settingsData.logo_url) {
         setCompanyLogo(settingsData.logo_url);
         localStorage.setItem("company_logo", settingsData.logo_url);
      }

      if (banquetsData) {
        const mappedBanquets = banquetsData.map(b => {
          const restoredItems = (b.banquet_items || []).map(item => {
            const originalDish = dishesData?.find(d => d.id === item.dish_id);
            if (!originalDish && !item.item_name) return null;

            return {
              id: originalDish?.id || item.dish_id || 0,
              name: item.item_name || originalDish?.name || "Удаленное блюдо",
              price: item.item_price ? Number(item.item_price) : (originalDish?.price || 0),
              weight: originalDish?.weight || "-",
              image_url: originalDish?.image_url || null,
              uid: item.id,
              quantity: item.quantity,
              comment: item.comment,
              groupId: item.group_id,
              category_name: item.group_id
            };
          }).filter(Boolean);

          return {
             ...b,
             id: b.id,
             name: b.client_name || "Без имени",
             date: b.event_date,
             hall: b.hall_name,
             startTime: b.start_time,
             eventType: b.event_type,
             servicePercent: b.service_percent,
             techEquipment: b.tech_equipment,
             guests: b.guests_count,
             prepayment: b.prepayment,
             status: b.status,
             isPinned: b.is_pinned,
             pricePerPerson: b.price_per_person, 
             items: restoredItems,
             categoryNotes: b.category_notes || {},
             groupOrder: b.group_order || [],
             customTotalMenu: b.custom_total_menu, 
             hideTotalMenu: b.hide_total_menu,
             customTotalPrice: b.custom_total_price, 
             hideTotalPrice: b.hide_total_price
          };
        });
        setBanquets(mappedBanquets);
      }

    } catch (error) {
      console.error("Ошибка при загрузке:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // --- Логотип сохраняем локально ---
  useEffect(() => {
  const fetchSettings = async () => {
    const { data, error } = await supabase
      .from('settings')
      .select('logo_url')
      .eq('id', 1)
      .single();

    if (!error && data && data.logo_url) {
      setCompanyLogo(data.logo_url); // Убедитесь, что у вас есть стейт setCompanyLogo
    }
  };

  fetchSettings();
}, []);

  // --- ФУНКЦИЯ ВЫХОДА ---
  const logout = async () => { 
      await supabase.auth.signOut();
      setView("list"); 
  };

  // --- КОМПОНЕНТ ВХОДА (LOGIN) ---
  const LoginScreen = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            setErrorMsg("Ошибка входа: Неверный Email или Пароль");
        }
        setLoading(false);
    };

    return (
      <div style={{ minHeight: "100vh", backgroundColor: styles.colors.bg, paddingTop: "1px", color: styles.colors.text, transition: "0.3s" }}>
        <div style={{ ...styles.card, maxWidth: "380px", margin: isMobile ? "40px auto" : "80px auto", textAlign: "center", width: "90%" }}>
          <TopBar lang={lang} setLang={setLang} isDark={isDark} setIsDark={setIsDark} styles={styles} />
          
          <div style={{ display: "inline-flex", padding: "10px", borderRadius: "50%", marginBottom: "20px", width: "80px", height: "80px", overflow: "hidden", alignItems: 'center', justifyContent: 'center' }}>
            {companyLogo ? <img src={companyLogo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <ChefHat size={50} color="#3b82f6" />}
          </div>
          
          <h2 style={{ marginTop: 0 }}>{t.appTitle}</h2>
          <p style={{ color: styles.colors.muted, marginBottom: '20px' }}></p>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <input 
                type="email" 
                placeholder="Email" 
                style={styles.input} 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input 
                type="password" 
                placeholder={t.password} 
                style={styles.input} 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              
              {errorMsg && <div style={{ color: '#ef4444', fontSize: '14px' }}>{errorMsg}</div>}

              <button 
                type="submit" 
                style={{ ...styles.btn, ...styles.btnPrimary, justifyContent: 'center', padding: '12px' }} 
                disabled={loading}
              >
                {loading ? "Вход..." : t.loginBtn}
              </button>
          </form>
        </div>
      </div>
    );
  };

  // --- ЕСЛИ НЕТ СЕССИИ - ПОКАЗЫВАЕМ ВХОД ---
  if (!session) return <LoginScreen />;

  // --- ЕСЛИ ЗАГРУЗКА ДАННЫХ ---
  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: styles.colors.bg, color: styles.colors.text }}>
         {/* 3. Используем перевод для текста загрузки */}
         <h3>{t.loading || "Loading..."}</h3>
      </div>
    );
  }

  // --- ОСНОВНОЙ ИНТЕРФЕЙС ---
  return (
    <>
      <PrintStyles />
      <div style={{ position: "fixed", top: "10px", right: "10px", zIndex: 100 }}>
        <TopBar lang={lang} setLang={setLang} isDark={isDark} setIsDark={setIsDark} styles={styles} />
      </div>

      {view === "admin" ? (
        <AdminPanel
          dishes={dishes} setDishes={setDishes}
          categories={categories} setCategories={setCategories}
          halls={halls} setHalls={setHalls}
          setView={setView} isMobile={isMobile} styles={styles} t={t}
          companyLogo={companyLogo} setCompanyLogo={setCompanyLogo}
          banquets={banquets} setBanquets={setBanquets}
        />
      ) : view === "editor" ? (
        <BanquetEditor
          currentBanquet={currentBanquet} setCurrentBanquet={setCurrentBanquet}
          dishes={dishes} setDishes={setDishes}
          categories={categories} setCategories={setCategories}
          halls={halls}
          setView={setView} banquets={banquets} setBanquets={setBanquets}
          isMobile={isMobile} styles={styles} t={t} isDark={isDark}
          companyLogo={companyLogo}
        />
      ) : (
        <BanquetList
          banquets={banquets} setCurrentBanquet={setCurrentBanquet}
          setView={setView} 
          currentUser={session.user.email} 
          logout={logout}
          styles={styles} t={t} setBanquets={setBanquets}
          companyLogo={companyLogo} halls={halls}
        />
      )}
    </>
  );
}